import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { CountriesService } from '~shared/services/countries.service';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { IAdvancedFormConfig } from '~shared/modules/advanced-form/models/advanced-form.model';

import { AccountInfoUtil } from '../utils/account-info.util';
import { AccountFormsConfigUtil } from '../utils/account-forms-config.util';
import { AccountManagementHttpService } from './account-management-http.service';
import { IAccountDetailsSection } from '../models/account-info.model';

@Injectable()
export class AccountManagementDataService {
  constructor(
    private accountInfoService: AccountInfoService,
    private accountManagementHttpService: AccountManagementHttpService,
    private countriesHttpService: CountriesService,
  ) {}

  public getAdvancedFormConfigForChangeEmail(): Observable<IAdvancedFormConfig> {
    return this.accountInfoService.getAccountInfo().pipe(
      take(1),
      map(({ email }) => AccountFormsConfigUtil.createChangeEmailConfig(email)),
    );
  }

  public getAdvancedFormConfigForBusinessInfo(): Observable<IAdvancedFormConfig> {
    const businessInfo$ = this.accountManagementHttpService.getBusinessInfo();
    return combineLatest([businessInfo$, this.countriesHttpService.countries$]).pipe(
      take(1),
      map(([businessInfo, countries]) => {
        return AccountFormsConfigUtil.createEditBusinessInfoConfig(businessInfo, countries);
      }),
    );
  }

  public getAdvancedFormConfigForAddBusinessInfo(): Observable<IAdvancedFormConfig> {
    const businessInfo$ = this.accountManagementHttpService.getBusinessInfo();
    return combineLatest([businessInfo$, this.countriesHttpService.countries$]).pipe(
      take(1),
      map(([businessInfo, countries]) =>
        AccountFormsConfigUtil.createAddBusinessInfoConfig(businessInfo, countries)
      ),
    );
  }

  public getAdvancedFormConfigForPrivateInfo(): Observable<IAdvancedFormConfig> {
    return this.accountManagementHttpService.getPrivateInfo().pipe(
      map((privateInfo) => {
        return AccountFormsConfigUtil.createPrivateInfoConfig(privateInfo);
      }),
    );
  }

  public getAccountDetailsList(): Observable<IAccountDetailsSection[]> {
    const accountInfo$ = combineLatest([
      this.accountInfoService.isEntrepreneur(),
      this.accountManagementHttpService.userInfo$,
      this.countriesHttpService.countries$,
    ]);

    return accountInfo$.pipe(
      map(([isEntrepreneur, userInfo, countries]) => {
        return AccountInfoUtil.generateAccountDetailsList(isEntrepreneur, userInfo, countries);
      }),
      take(1),
    );
  }
}
